import { ref, computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { useRouter } from 'vue-router'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreNotifications } from '@/stores/storeNotifications'
import { useStoreFeed } from '@/stores/storeFeed'
import { useStoreModals } from '@/stores/storeModals'
import { supabase } from '@/supabase'

// Reference to storeFeed for push notification handlers
let storeFeedRef = null

// Persisted so logout can delete the backend row even when the in-memory
// token was never re-emitted this launch
const STORED_PUSH_TOKEN_KEY = 'spectra_push_token'

// Global state for push notification management
const pushToken = ref(null)
const permissionStatus = ref(null)
const initialized = ref(false)
const registrationError = ref(null)

export function usePushNotifications() {
  const router = useRouter()

  const isNativeApp = computed(() => {
    return Capacitor.isNativePlatform()
  })

  const isIOS = computed(() => {
    return Capacitor.getPlatform() === 'ios'
  })

  const canUsePush = computed(() => {
    return isNativeApp.value
  })

  const hasPermission = computed(() => {
    return permissionStatus.value === 'granted'
  })

  /**
   * Initialize push notifications
   * Should be called after user authentication
   */
  const initPushNotifications = async () => {
    if (!canUsePush.value) {
      console.log('[Push] Skipping init - not a native app')
      return
    }

    if (initialized.value) {
      console.log('[Push] Already initialized')
      return
    }

    const storeAuth = useStoreAuth()
    if (!storeAuth.user) {
      console.log('[Push] Skipping init - user not authenticated')
      return
    }

    console.log('[Push] Initializing push notifications...')

    try {
      // Check current permission status (but don't auto-request)
      const permResult = await PushNotifications.checkPermissions()
      permissionStatus.value = permResult.receive

      console.log('[Push] Current permission status:', permResult.receive)

      // Set up event listeners first (so we can receive token when it comes)
      setupListeners()

      // If already granted, register for push
      if (permissionStatus.value === 'granted') {
        await registerForPush()
      } else if (permissionStatus.value === 'prompt') {
        // First run after sign-in: ask once. A denial is respected until
        // the user changes it in system settings.
        const requestResult = await PushNotifications.requestPermissions()
        permissionStatus.value = requestResult.receive
        if (permissionStatus.value === 'granted') {
          await registerForPush()
        }
      } else {
        console.log(
          '[Push] Permission not granted, skipping registration. Use requestPermission() to request.'
        )
      }

      initialized.value = true
    } catch (error) {
      console.error('[Push] Error initializing:', error)
      registrationError.value = error.message
    }
  }

  /**
   * Register device for push notifications
   */
  const registerForPush = async () => {
    try {
      console.log('[Push] Registering for push notifications...')
      await PushNotifications.register()
    } catch (error) {
      console.error('[Push] Registration error:', error)
      registrationError.value = error.message
    }
  }

  /**
   * Set up push notification event listeners
   */
  const setupListeners = () => {
    // Registration success - we get the device token
    PushNotifications.addListener('registration', async (token) => {
      console.log(
        '[Push] Registration successful, token:',
        token.value?.substring(0, 20) + '...'
      )
      pushToken.value = token.value

      // Save token to backend
      await saveTokenToBackend(token.value)
    })

    // Registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push] Registration error:', error)
      registrationError.value = error.error
    })

    // Notification received while app is in foreground
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        console.log('[Push] Notification received in foreground:', notification)

        // Update unread count in notifications store
        const storeNotifications = useStoreNotifications()
        storeNotifications.fetchUnreadCount()

        // Refresh feed store to get new items and update actionable count
        if (!storeFeedRef) {
          storeFeedRef = useStoreFeed()
        }
        storeFeedRef.fetchActionableUnreadCount()
        storeFeedRef.refresh()
      }
    )

    // Notification tapped (app was in background or closed)
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        console.log('[Push] Notification tapped:', action)

        // Refresh feed store when app comes from background via notification
        if (!storeFeedRef) {
          storeFeedRef = useStoreFeed()
        }
        storeFeedRef.fetchActionableUnreadCount()
        storeFeedRef.refresh()

        // Extract data from notification
        const data = action.notification?.data || {}

        // Navigate based on notification type
        handleNotificationNavigation(data)
      }
    )
  }

  /**
   * Save push token to backend
   */
  const saveTokenToBackend = async (token) => {
    const storeAuth = useStoreAuth()
    console.log('[Push] Attempting to save token, user:', storeAuth.user?.id)

    if (!storeAuth.user) {
      console.warn('[Push] Cannot save token - no authenticated user')
      return
    }

    try {
      const platform = isIOS.value ? 'ios' : 'android'
      console.log('[Push] Saving token for platform:', platform)

      // Upsert the token
      const { data, error } = await supabase.from('push_tokens').upsert(
        {
          user_id: storeAuth.user.id,
          token: token,
          platform: platform,
          last_used_at: new Date().toISOString()
        },
        {
          onConflict: 'user_id,token'
        }
      )

      if (error) {
        console.error(
          '[Push] Error saving token:',
          error.message,
          error.details,
          error.hint
        )
      } else {
        localStorage.setItem(STORED_PUSH_TOKEN_KEY, token)
        console.log('[Push] Token saved successfully', data)
      }
    } catch (error) {
      console.error('[Push] Error saving token to backend:', error)
    }
  }

  /**
   * Handle navigation when notification is tapped
   */
  const handleNotificationNavigation = (data) => {
    console.log('[Push] Handling navigation with data:', data)

    // Navigate based on notification data
    if (data.type === 'feed_notification' || data.feed_filter) {
      // Navigate to feed with the specified filter (e.g., SYSTEM)
      const storeFeed = useStoreFeed()
      const filter = data.feed_filter || 'SYSTEM'
      console.log('[Push] Navigating to feed with filter:', filter)

      // Set the filter in the store before navigating
      storeFeed.setFilter(filter)

      // Navigate to the activity/feed page
      router.push({ name: 'WebActivity' })
    } else if (data.type === 'message_received' && data.conversation_id) {
      // New message - open the conversation
      const storeModals = useStoreModals()
      storeModals.openModal({
        type: 'Messages',
        props: { conversationId: data.conversation_id }
      })
    } else if (data.type === 'reaction_received' || data.type === 'comment_received') {
      // Reactions and comments on reviews - navigate to feed
      router.push({ name: 'WebActivity' })
    } else if (data.type?.startsWith('list_') && data.list_id) {
      router.push({ name: 'WebList', params: { id: data.list_id } })
    } else if (data.club_slug) {
      router.push({ name: 'WebClubDetail', params: { slug: data.club_slug } })
    } else if (data.club_id) {
      router.push({ name: 'WebClubs' })
    } else {
      // Default: go to feed
      router.push({ name: 'WebActivity' })
    }
  }

  /**
   * Remove push token on logout. Must run while the session is still valid
   * (storeAuth.clearSession calls this before signOut). Falls back to the
   * persisted token when the in-memory ref was never populated this launch.
   */
  const unregisterPush = async () => {
    const storeAuth = useStoreAuth()
    const token =
      pushToken.value || localStorage.getItem(STORED_PUSH_TOKEN_KEY)

    try {
      // Remove token from backend
      if (token && storeAuth.user) {
        await supabase
          .from('push_tokens')
          .delete()
          .eq('user_id', storeAuth.user.id)
          .eq('token', token)
      }

      if (canUsePush.value) {
        await PushNotifications.removeAllListeners()
      }

      localStorage.removeItem(STORED_PUSH_TOKEN_KEY)
      pushToken.value = null
      initialized.value = false

      console.log('[Push] Unregistered successfully')
    } catch (error) {
      console.error('[Push] Error unregistering:', error)
    }
  }

  /**
   * Request permission (for settings page)
   * This will prompt the user for permission if not already determined
   */
  const requestPermission = async () => {
    if (!canUsePush.value) {
      console.log('[Push] Cannot use push - not native platform')
      return false
    }

    const storeAuth = useStoreAuth()
    if (!storeAuth.user) {
      console.log('[Push] Cannot request permission - user not authenticated')
      return false
    }

    try {
      console.log('[Push] Requesting permission...')

      // Ensure listeners are set up first
      if (!initialized.value) {
        console.log('[Push] Setting up listeners before permission request')
        setupListeners()
      }

      const result = await PushNotifications.requestPermissions()
      permissionStatus.value = result.receive
      console.log('[Push] Permission result:', result.receive)

      if (result.receive === 'granted') {
        console.log('[Push] Permission granted, registering for push...')
        await registerForPush()
        initialized.value = true
        return true
      }

      return false
    } catch (error) {
      console.error('[Push] Error requesting permission:', error)
      registrationError.value = error.message
      return false
    }
  }

  /**
   * Force re-registration (for debugging)
   * Resets state and attempts fresh registration
   */
  const forceReregister = async () => {
    if (!canUsePush.value) return false

    const storeAuth = useStoreAuth()
    if (!storeAuth.user) {
      console.log('[Push] Cannot register - user not authenticated')
      return false
    }

    console.log('[Push] Force re-registering...')

    // Reset state
    pushToken.value = null
    registrationError.value = null
    initialized.value = false

    try {
      // Remove existing listeners to avoid duplicates
      await PushNotifications.removeAllListeners()

      // Set up fresh listeners
      setupListeners()

      // Check permission
      const permResult = await PushNotifications.checkPermissions()
      permissionStatus.value = permResult.receive
      console.log('[Push] Current permission:', permResult.receive)

      if (permResult.receive === 'granted') {
        await registerForPush()
        initialized.value = true
        return true
      } else {
        console.log('[Push] Permission not granted, cannot register')
        return false
      }
    } catch (error) {
      console.error('[Push] Force re-registration error:', error)
      registrationError.value = error.message
      return false
    }
  }

  return {
    // State
    pushToken: computed(() => pushToken.value),
    permissionStatus: computed(() => permissionStatus.value),
    hasPermission,
    canUsePush,
    initialized: computed(() => initialized.value),
    registrationError: computed(() => registrationError.value),

    // Methods
    initPushNotifications,
    unregisterPush,
    requestPermission,
    forceReregister
  }
}
